import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { JSX } from "react";
import { User } from "./DataModels"
import { SearchBox } from "../SearchBox/SearchBox";
import { PagedResponse } from "@iconics/core";

export class UserPicker extends BaseComponent<IUserPickerProps, IUserPickerState>
{
    private searchUsersTimer: NodeJS.Timeout | null = null;
    private get noUserSelectedError() { return (!this.props.searchText || this.state.selectedUser ? "" : (this.props.noUserSelectedLabel ?? UserPicker.noUserSelectedLabel)); }

    public static label = '';
    public static noUserSelectedLabel = '';

    constructor(props: IUserPickerProps)
    {
        super(props);
        this.state = {
            users: [],
            selectedUser: null,
            searchText: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        await this.searchTextUpdated();
    }

    private async searchTextUpdated(): Promise<void>
    {
        clearTimeout(this.searchUsersTimer ?? undefined);
        this.searchUsersTimer = setTimeout(() => this.searchUsers(), 500);
    }

    private async searchUsers(): Promise<void>
    {
        const { searchText } = this.state;
        const users = await this.onUserSearch(searchText);

        // important: we must only select a user if the label matches the search text because this is the only field that uniquely identifies the user
        const selectedUser = users.find(i => i.label.toLowerCase() === searchText.toLowerCase()) ?? null;

        await this.setStateAsync({ users: users, selectedUser: selectedUser });
        await this.props.onUserChange?.(selectedUser);
    }

    private async onUserSearch(searchText: string): Promise<IUserPickerUser[]>
    {
        if (!searchText)
        {
            return [];
        }
        const searchTerm = searchText.replace(/'/g, "''");

        const userData = await this.components.fadxPlatformApiClientV2.v2.security.byTenancy.users.get<PagedResponse<User[]>>({
            select: User,
            filter: `contains(concat(concat(concat(PreferredName, ' ('), Email), ')'),'${searchTerm}')`,
            tenancy: this.props.tenantId,
            orderBy: 'PreferredName,Email',
            top: 10
        });

        const users = userData.value.map(i => ({
            label: ((i.PreferredName ?? '') + (i.Email ? ` (${i.Email})` : '')).trim(),
            email: i.Email ?? '',
            userName: i.UserName ?? '',
            firstName: i.FirstName ?? '',
            lastName: i.LastName ?? '',
            displayName: i.DisplayName ?? '',
            preferredName: i.PreferredName ?? '',
            userType: i.UserType ?? '',
        }));

        return users;
    }

    private async searchTextChanged(text: string): Promise<void>
    {
        await this.setStateAsync({ searchText: text });
        await this.searchTextUpdated();
        await this.props.onChange(text);
    }

    public render(): JSX.Element
    {
        const { width } = this.props;
        return (
            <div style={{ ...(width != null && { width: width }) }}>
                <SearchBox
                    disabled={this.props.disabled ?? false}
                    items={this.state.users.map(u => u.label)}
                    placeholder={this.props.placeholder}
                    onInputValueChange={e => this.searchTextChanged(e.inputValue)}
                    label={this.props.label ?? UserPicker.label} />
                <div className="text-danger">{this.noUserSelectedError}</div>
            </div>
        );
    }
}

export interface IUserPickerProps
{
    noUserSelectedLabel?: string;
    disabled?: boolean;
    width?: string;
    searchText: string;
    label?: string;
    placeholder?: string;
    alternateStyle?: boolean;
    error?: boolean;
    helperText?: string;
    tenantId: string;
    onChange: (searchText: string) => Promise<void>;
    onUserChange?: (user: IUserPickerUser | null) => Promise<void>;
}

export interface IUserPickerState
{
    users: IUserPickerUser[];
    selectedUser: IUserPickerUser | null;
    searchText: string;
}

export interface IUserPickerUser
{
    label: string;
    email: string;
    userName: string;
    firstName: string;
    lastName: string;
    displayName: string;
    preferredName: string;
    userType: string;
}
